/**
 * URL encoding for directory lists (releases, projects, wiki, …).
 * Work-item queues keep using FilterSpec in filters.ts.
 */

export type DirectoryQuery<K extends string> = {
  query: string | null;
  orderBy: string | null;
  selected: Record<K, string[]>;
};

export function parseCsvParam(raw: string | null): string[] {
  if (raw == null || raw === '') return [];
  return raw.split(',').map((part) => part.trim()).filter((part) => part.length > 0);
}

export function searchParamsToDirectoryQuery<K extends string>(
  search: URLSearchParams,
  chipKeys: readonly K[],
  defaultOrderBy: string | null,
): DirectoryQuery<K> {
  const selected = {} as Record<K, string[]>;
  for (const key of chipKeys) {
    selected[key] = parseCsvParam(search.get(key));
  }
  const queryRaw = search.get('query');
  const query = queryRaw != null && queryRaw.trim().length > 0 ? queryRaw.trim() : null;
  const orderRaw = search.get('order_by');
  const orderBy = orderRaw && orderRaw.length > 0 ? orderRaw : defaultOrderBy;
  return { query, orderBy, selected };
}

export function directoryQueryToSearchParams<K extends string>(
  current: URLSearchParams,
  next: DirectoryQuery<K>,
  chipKeys: readonly K[],
  defaultOrderBy: string | null,
): URLSearchParams {
  const params = new URLSearchParams(current);
  if (next.query) params.set('query', next.query);
  else params.delete('query');
  for (const key of chipKeys) {
    const values = next.selected[key] ?? [];
    if (values.length > 0) params.set(key, values.join(','));
    else params.delete(key);
  }
  if (next.orderBy && next.orderBy !== defaultOrderBy) params.set('order_by', next.orderBy);
  else params.delete('order_by');
  return params;
}

export function directoryQueryIsActive<K extends string>(
  state: DirectoryQuery<K>,
  chipKeys: readonly K[],
  defaultOrderBy: string | null,
): boolean {
  if (state.query) return true;
  if (state.orderBy && state.orderBy !== defaultOrderBy) return true;
  return chipKeys.some((key) => (state.selected[key] ?? []).length > 0);
}

export function toggleSelected(current: string[], value: string): string[] {
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}
